class SportwristxEnhance extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-enhanced');
  }
}

if (!customElements.get('sportwristx-enhance')) {
  customElements.define('sportwristx-enhance', SportwristxEnhance);
}

class SportwristxMobileAtc extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button[type="submit"]');
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data.sectionId === this.dataset.section) {
        this.toggleAvailability(event.data.variant.available);
        const price = event.data.html?.querySelector('.swx-mobile-atc__summary strong');
        if (price) this.querySelector('.swx-mobile-atc__summary strong').textContent = price.textContent;
      }
    });
    this.variantUnavailableUnsubscriber = subscribe(PUB_SUB_EVENTS.variantUnavailable, (event) => {
      if (event.data.sectionId === this.dataset.section) {
        this.toggleAvailability(false);
      }
    });
    const primaryButton = this.closest('product-info')?.querySelector('button[type="submit"][name="add"]');
    if (primaryButton) {
      this.hidden = true;
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
      this.visibilityObserver.observe(primaryButton);
    }
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
    this.variantUnavailableUnsubscriber?.();
    this.visibilityObserver?.disconnect();
  }

  toggleAvailability(available) {
    this.button.toggleAttribute('disabled', !available);
    this.button.setAttribute('aria-disabled', String(!available));
  }
}

if (!customElements.get('sportwristx-mobile-atc')) {
  customElements.define('sportwristx-mobile-atc', SportwristxMobileAtc);
}

const swxMotionSelector = '[data-swx-reveal]';

function initializeSportwristxMediaFade(root = document) {
  const images = [
    ...(root.matches?.('.swx-media-fade') ? [root] : []),
    ...root.querySelectorAll('.swx-media-fade'),
  ].filter((image) => !image.dataset.swxMediaFadeBound);

  images.forEach((image) => {
    image.dataset.swxMediaFadeBound = 'true';
    const reveal = () => image.classList.add('is-loaded');
    image.addEventListener('load', reveal, { once: true });
    image.addEventListener('error', reveal, { once: true });
    if (image.complete) requestAnimationFrame(reveal);
  });
}

function initializeSportwristxMotion(root = document) {
  const candidates = [
    ...(root.matches?.(swxMotionSelector) ? [root] : []),
    ...root.querySelectorAll(swxMotionSelector),
  ].filter((element) => !element.dataset.swxMotionBound);

  if (!candidates.length) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  candidates.forEach((element, index) => {
    element.dataset.swxMotionBound = 'true';
    const requestedDelay = Number(element.dataset.swxRevealDelay);
    const delay = Number.isFinite(requestedDelay) ? requestedDelay : Math.min(index * 80, 240);
    element.style.setProperty('--swx-reveal-delay', `${delay}ms`);
  });

  candidates.forEach((element) => element.classList.add('swx-motion-ready'));

  if (reduceMotion || !('IntersectionObserver' in window)) {
    candidates.forEach((element) => element.classList.add('swx-motion-ready', 'is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const element = entry.target;
      requestAnimationFrame(() => element.classList.add('is-visible'));
      observer.unobserve(element);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.14 });

  candidates.forEach((element) => observer.observe(element));
}

initializeSportwristxMotion();
initializeSportwristxMediaFade();
document.addEventListener('shopify:section:load', (event) => {
  initializeSportwristxMotion(event.target);
  initializeSportwristxMediaFade(event.target);
});

const megaMenuHoverMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

function initializeSportwristxMegaMenuHover(root = document) {
  const menus = [
    ...(root.matches?.('header-menu') ? [root] : []),
    ...root.querySelectorAll('header-menu'),
  ].filter((menu) => !menu.dataset.swxHoverBound);

  menus.forEach((menu) => {
    const details = menu.querySelector('details');
    const summary = details?.querySelector('summary');
    if (!details || !summary) return;
    menu.dataset.swxHoverBound = 'true';

    menu.addEventListener('pointerenter', () => {
      if (!megaMenuHoverMedia.matches) return;
      window.clearTimeout(menu.swxCloseTimer);
      document.querySelectorAll('header-menu details[open]').forEach((openDetails) => {
        if (openDetails === details) return;
        openDetails.removeAttribute('open');
        openDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      });
      details.setAttribute('open', '');
      summary.setAttribute('aria-expanded', 'true');
    });

    menu.addEventListener('pointerleave', () => {
      if (!megaMenuHoverMedia.matches) return;
      menu.swxCloseTimer = window.setTimeout(() => {
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
      }, 300);
    });
  });
}

initializeSportwristxMegaMenuHover();
document.addEventListener('shopify:section:load', (event) => initializeSportwristxMegaMenuHover(event.target));

const footerMedia = window.matchMedia('(max-width: 749px)');
const syncFooterGroups = () => {
  document.querySelectorAll('[data-swx-footer-group]').forEach((group) => {
    group.open = !footerMedia.matches;
  });
};

document.addEventListener('DOMContentLoaded', syncFooterGroups, { once: true });
footerMedia.addEventListener('change', syncFooterGroups);
